import React from 'react';
import {useQuery} from '@apollo/client';
import {useNavigate, useParams} from 'react-router-dom';
import {Formik} from 'formik';
import {Box, Chip, Grid, Typography} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterMoment} from '@mui/x-date-pickers-pro/AdapterMoment';
import {CONTACT_PAGE_QUERY} from '../../pages/AccountContactSearchPage/contactQueries';
import Loading from '../Loading/Loading';
import {
  ContactPageQuery,
  ContactPageQueryVariables,
  ContactPageQuery_contactDetails,
  ContactPageQuery_contactDetails_accountContacts_account,
} from '../../schema-types/ContactPageQuery';
import {Panel} from '../Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {FormField, ActionType, AutoField} from '../AutoForm/AutoFieldNew';
import withAutoFormNew, {AutoFormProps, FormReadOnly} from '../AutoForm/AutoFormNew';
import {clientTypeColors, contactTypeColors} from './contactStyles';
import {SearchResults} from '../Common/SearchResults';
import {RenderColor} from '../EditorGrid/GridRenderComponents';
import {RenderAccountNameAndEmail} from '../../pages/AccountSearchPage/AccountSearchPage';
import {titleField} from '../../pages/AddExtAccountPage/addAccountFields';
import {getContactName} from '../../utils/getContactName';
import useTitle from '../../hooks/useTitle';
import {VulnerableClientBanner} from '../VulnerableClientBanner/VulnerableClientBanner';
import {SimpleVulnerabilityFields} from './SimpleVulnerabilityFields';
import {ContactVulnerabilities} from './ContactVulnerabilities';
import {SimpleInvestmentPhilosophyFields} from './SimpleInvestmentPhilosophyFields';
import validateUUID from '../../utils/validateUUID';

export const ContactPage: React.FC = () => {
  const {contactId} = useParams();
  const {data, loading} = useQuery<ContactPageQuery, ContactPageQueryVariables>(
    CONTACT_PAGE_QUERY,
    {
      variables: {
        contactId: validateUUID(contactId)!,
      },
      skip: !contactId,
    }
  );
  if (!contactId) {
    return <div>Sorry no contact id in URL</div>;
  }
  if (loading) {
    return <Loading />;
  }
  if (!data?.contactDetails) {
    return <div>No contact found</div>;
  }
  const contact = data.contactDetails;
  return (
    <Formik initialValues={{}} onSubmit={() => {}}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Grid container>
          <Grid item xs={12}>
            <VulnerableClientBanner
              contacts={data.contactDetails ? [data.contactDetails] : []}
            />
          </Grid>
          <Grid item xs={8}>
            <Chip
              label={contact.contactStatus}
              sx={{
                background:
                  contactTypeColors[
                    (contact.contactStatus as keyof typeof contactTypeColors) || 'Contact'
                  ],
              }}
            />
            <Box mt={1} />
            <Typography color="primary" variant="h3">
              {getContactName(contact)} {contact.isDeleted ? '(deleted)' : ''}
            </Typography>
          </Grid>
          <Grid item />
        </Grid>
        <Box mt={4} />
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FormReadOnly readOnly>
              <ContactPageDetails
                contact={data.contactDetails}
                validate={() => ({})}
                formFields={[
                  ...summaryContactFormFields,
                  ...mainContactFormFields,
                  ...relationshipContactFormFields,
                ]}
                submit={() => {}}
                initialValueOverrides={contact as any}
              />
            </FormReadOnly>
          </Grid>
          {process.env.NODE_ENV === 'development' ? (
            <Grid item xs={12} spacing={2}>
              <Grid container>
                <Grid item xs={4}>
                  <div>Debug:</div>
                </Grid>
                <Grid item xs={4}>
                  OneView Id: <br />
                  {data.contactDetails.id}
                </Grid>
                <Grid item xs={4}>
                  Curo Id: <br />
                  {data.contactDetails.curoId}
                </Grid>
              </Grid>
            </Grid>
          ) : null}
        </Grid>
        <Box mt={12} />
      </LocalizationProvider>
    </Formik>
  );
};

export const ContactPageDetails = withAutoFormNew(
  ({formFields, contact}: ContactFormProps) => {
    useTitle(`Client - ${contact.name}`);
    return (
      <>
        <Panel title="Contact information" color={themeColors.green}>
          <Box mt={2} />
          <Grid spacing={4} wrap="wrap" container>
            {mainContactFormFields.map((field) => (
              <Grid xs={4} item key={field.id}>
                <AutoField field={field} />
              </Grid>
            ))}
            <Grid xs={4} item key="country">
              <AutoField
                field={{
                  id: 'country',
                  label: 'Country',
                  type: ActionType.text,
                  readOnly: true,
                  initialValue: contact.addresses.map((x) => x.country).join(','),
                }}
              />
            </Grid>
          </Grid>
          <Box mt={4} />
        </Panel>
        <Box mt={4} />
        <Grid item xs={12}>
          <ContactAccounts contact={contact} />
        </Grid>
        <Box mt={4} />
        <ContactVulnerabilities contact={contact} />
        <Box mt={4} />
        <Panel title="Vulnerability details" color={themeColors.coral}>
          <Box mt={2} />
          <SimpleVulnerabilityFields contact={contact} />
          <Box mt={2} />
        </Panel>
        <Box mt={4} />
        <Panel title="Investment details" color={themeColors.coral}>
          <Box mt={2} />
          <SimpleInvestmentPhilosophyFields />
          <Box mt={2} />
        </Panel>
        <Box mt={4} />
        <Panel title="Relationship information" color={themeColors.coral}>
          <Box mt={2} />
          <Grid spacing={4} wrap="wrap" container>
            {relationshipContactFormFields.map((field) => (
              <Grid xs={4} item key={field.id}>
                <AutoField field={field} />
              </Grid>
            ))}
          </Grid>
          <Box mt={2} />
        </Panel>
      </>
    );
  }
);

type AccountColumnProps =
  GridColDef<ContactPageQuery_contactDetails_accountContacts_account> & {
    field: keyof ContactPageQuery_contactDetails_accountContacts_account;
  };

const contactPageAccountColumns: AccountColumnProps[] = [
  {
    field: 'name',
    headerName: ' ',
    flex: 2,
    renderCell: RenderAccountNameAndEmail,
  },
  {
    field: 'city',
    headerName: 'City',
    flex: 1,
  },
  {
    field: 'adviser',
    headerName: 'Adviser',
    flex: 1,
    valueGetter: (value, row, column, apiRef) => {
      return row.adviser?.name;
    },
  },
  {
    field: 'primaryContact' as any,
    headerName: 'Primary contact',
    valueGetter: (value, row, column, apiRef) => {
      return row.primaryContact?.name;
    },
    flex: 1,
  },
  {
    field: 'secondaryContact' as any,
    headerName: 'Secondary contact',
    valueGetter: (value, row, column, apiRef) => {
      return row.secondaryContact?.name;
    },
    flex: 1,
  },
  {
    field: 'clientType',
    headerName: 'Client Type',
    renderCell: RenderColor(clientTypeColors),
    flex: 1,
    valueGetter: (value, row, column, apiRef) => {
      return row.clientType;
    },
  },
  {
    field: 'primaryPhone',
    headerName: 'Phone',
    flex: 1,
  },
];

const ContactAccounts: React.FC<{
  contact: ContactPageQuery_contactDetails;
}> = ({contact}) => {
  const navigate = useNavigate();
  return (
    <>
      <SearchResults
        dataGridId="ContactAccounts"
        minHeight={false}
        hideFooter
        title="Accounts"
        loading={false}
        search=""
        columns={contactPageAccountColumns}
        data={contact.accountContacts.map((x) => x.account) || []}
        onRowDoubleClick={(row) => {
          navigate({pathname: `/account/${row.id}`});
        }}
      />
    </>
  );
};

type ContactFormProps = AutoFormProps<contactFormFields> & {
  readOnly?: boolean;
  contact: ContactPageQuery_contactDetails;
};

type ContactField = FormField & {
  id: keyof ContactPageQuery_contactDetails;
};

type contactFormFields = Partial<ContactPageQuery_contactDetails>;

export const summaryContactFormFields: ContactField[] = [
  {
    id: 'contactStatus',
    label: 'Contact status',
    type: ActionType.text,
    readOnly: true,
  },
];

export const mainContactFormFields: ContactField[] = [
  {
    id: 'name',
    label: 'Name',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'clientNumber',
    label: 'Client number',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'knownAs',
    label: 'Known as',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'gender',
    label: 'Gender',
    type: ActionType.select,
    options: [
      {label: 'Male', value: 'Male'},
      {label: 'Female', value: 'Female'},
      {label: 'Non-Binary', value: 'Non-Binary'},
      {label: 'Unknown', value: 'Unknown'},
    ],

    readOnly: true,
  },
  {...titleField, id: 'salutation', label: 'Title'},
  {
    id: 'primaryPhone',
    label: 'Primary phone (mobile)',
    phoneNumber: true,
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'homePhone',
    label: 'Home phone',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'secondaryPhone',
    label: 'Secondary phone',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'workPhone',
    label: 'Work phone',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'email',
    label: 'Email',
    type: ActionType.text,
    readOnly: true,
    private: true,
  },

  {
    id: 'jobTitle',
    label: 'Job title',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'addressee',
    label: 'Addressee',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'dob',
    label: 'Date of birth',
    type: ActionType.datepicker,
    fullWidth: true,
    readOnly: true,
  },
  {
    id: 'nationalInsurance',
    label: 'National Insurance number',
    type: ActionType.text,
    readOnly: true,
    private: true,
  },
  {
    id: 'nationality',
    label: 'Nationality',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'fullAddress',
    label: 'Address',
    type: ActionType.text,
    multiline: true,
    readOnly: true,
  },
];

export const relationshipContactFormFields: ContactField[] = [
  {
    id: 'familyStatus',
    label: 'Family status',
    type: ActionType.select,
    options: [
      {label: 'Single', value: 'Single'},
      {label: 'Married', value: 'Married'},
      {label: 'Divorced', value: 'Divorced'},
      {label: 'Widowed', value: 'Widowed'},
      {label: 'Separated', value: 'Separated'},
      {label: 'Cohabiting', value: 'Cohabiting'},
      {label: 'Civil Partnership', value: 'Civil Partnership'},
      {label: 'Intend to Marry', value: 'Intend to Marry'},
    ],

    readOnly: true,
  },
  {
    id: 'spouseName',
    label: 'Spouse name',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'anniversary',
    label: 'Anniversary',
    type: ActionType.datepicker,
    // format: 'DD MMM yyyy',
    fullWidth: true,
    readOnly: true,
    // initialValue: null,
  },
];

export const extraContactFormFields: ContactField[] = [
  {
    id: 'id',
    label: 'OneView ID',
    type: ActionType.text,
    readOnly: true,
  },
  {
    id: 'curoId',
    label: 'Curo ID',
    type: ActionType.text,
    readOnly: true,
  },
];
